import { Box, Center, HStack, VStack } from '@chakra-ui/react';

import Button from 'components/Button';
import Icon from 'components/Icon';
import Text from 'components/Text';

import NodeTypeIcon from './NodeTypeIcon';
import {
  getDifficultyConfig,
  getNodeCompleteLabel,
  getNodeDescription,
  getNodeOpenLabel,
  getNodeSubmitRoute,
  getNodeTypeLabel,
  getNodeXpReward,
  MANUAL_COMPLETION_TYPES,
  NodeState,
} from './dashboardUtils';
import { getNodeRoute, JourneyNode } from './journeyPathConfig';

interface NodeDetailCardProps {
  selectedNode: JourneyNode | null;
  nodeState: NodeState | null;
  onNavigate: (route: string) => void;
  isManuallyCompleted?: boolean;
  onToggleManualCompletion?: (nodeId: string) => void;
}

const CARD_MIN_HEIGHT = '240px';

const WelcomeState = () => (
  <VStack spacing={3} py={4} align="center">
    <Box style={{ fontSize: '32px' }}>
      🗺️
    </Box>
    <Text fontSize="large" fontWeight="semibold">
      Welcome to Your Journey
    </Text>
    <Text fontSize="small" opacity="secondary" textAlign="center">
      Select any task on your journey map to see details and get started.
    </Text>
  </VStack>
);


const NodeSelectedState = ({
  node,
  nodeState,
  onNavigate,
  isManuallyCompleted,
  onToggleManualCompletion,
}: {
  node: JourneyNode;
  nodeState: NodeState | null;
  onNavigate: (route: string) => void;
  isManuallyCompleted?: boolean;
  onToggleManualCompletion?: (nodeId: string) => void;
}) => {
  const typeLabel = getNodeTypeLabel(node.type);
  const isNodeCompleted = nodeState === 'completed';
  // Always purple accent unless completed (green)
  const accentRaw = isNodeCompleted ? '#68D391' : '#B794F4';
  const accentRgb = isNodeCompleted ? '104,211,145' : '183,148,244';
  const diffConfig = getDifficultyConfig(node.difficulty);
  const description = getNodeDescription(node);
  const xp = getNodeXpReward(node.type, node.difficulty);
  const openRoute = getNodeRoute(node);
  const submitRoute = getNodeSubmitRoute(node);
  const isManualType = MANUAL_COMPLETION_TYPES.has(node.type);
  const openLabel = getNodeOpenLabel(node);
  const completeLabel = getNodeCompleteLabel(node);

  const handleComplete = () => {
    if (isManualType && onToggleManualCompletion) {
      onToggleManualCompletion(node.id);
    } else if (submitRoute) {
      onNavigate(submitRoute);
    }
  };

  return (
    <VStack spacing={4} align="stretch">
      {/* Type header with icon + XP badge top-right */}
      <HStack spacing={3} align="flex-start">
        <Center
          w="44px"
          h="44px"
          borderRadius="lg"
          flexShrink={0}
          style={{
            background: isNodeCompleted
              ? 'linear-gradient(145deg, rgba(104, 211, 145, 0.2) 0%, rgba(104, 211, 145, 0.08) 100%)'
              : `linear-gradient(145deg, rgba(${accentRgb}, 0.2) 0%, rgba(${accentRgb}, 0.08) 100%)`,
            border: isNodeCompleted
              ? '1px solid rgba(104, 211, 145, 0.3)'
              : `1px solid rgba(${accentRgb}, 0.3)`,
          }}
        >
          {isNodeCompleted ? (
            <Icon type="CheckColor" size={22} />
          ) : (
            <NodeTypeIcon type={node.type} size={22} color={accentRaw} />
          )}
        </Center>
        <VStack spacing={0} align="flex-start" flex={1}>
          <HStack spacing={2} align="center">
            <Box
              as="span"
              style={{
                fontSize: '11px',
                fontWeight: 700,
                textTransform: 'uppercase' as const,
                letterSpacing: '0.06em',
                color: accentRaw,
                lineHeight: '16px',
              }}
            >
              {isNodeCompleted ? 'Completed' : typeLabel}
            </Box>
            {!isNodeCompleted && diffConfig && (
              <Center
                px={1.5}
                borderRadius="full"
                style={{
                  backgroundColor: `${diffConfig.color}18`,
                  border: `1px solid ${diffConfig.color}40`,
                  height: '16px',
                }}
              >
                <Box
                  as="span"
                  style={{ fontSize: '9px', fontWeight: 700, color: diffConfig.color, lineHeight: 1 }}
                >
                  {diffConfig.label}
                </Box>
              </Center>
            )}
          </HStack>
          <Text fontSize="large" fontWeight="bold">
            {node.label}
          </Text>
        </VStack>

        {/* XP badge — top right */}
        {xp > 0 && (
          <Box
            px={2.5}
            py={1}
            borderRadius="full"
            flexShrink={0}
            style={{
              backgroundColor: isNodeCompleted
                ? 'rgba(104, 211, 145, 0.12)'
                : `rgba(${accentRgb}, 0.12)`,
              border: isNodeCompleted
                ? '1px solid rgba(104, 211, 145, 0.25)'
                : `1px solid rgba(${accentRgb}, 0.25)`,
            }}
          >
            <Box
              as="span"
              style={{
                fontSize: '12px',
                fontWeight: 700,
                color: accentRaw,
              }}
            >
              {isNodeCompleted ? `${xp} XP` : `+${xp} XP`}
            </Box>
          </Box>
        )}
      </HStack>

      {/* Description */}
      <Text fontSize="small" opacity="secondary" lineHeight="1.6">
        {description}
      </Text>

      {/* CTA buttons: stacked full-width */}
      <VStack spacing={2} pt={1}>
        <Button
          scheme="outline"
          size="sm"
          w="100%"
          onClick={() => onNavigate(openRoute)}
        >
          {openLabel}
        </Button>
        {isNodeCompleted || isManuallyCompleted ? (
          <HStack
            spacing={1.5}
            w="100%"
            justify="center"
            py={2}
            borderRadius="md"
            style={{
              backgroundColor: 'rgba(104, 211, 145, 0.12)',
              border: '1px solid rgba(104, 211, 145, 0.3)',
            }}
          >
            <Icon type="CheckColor" size={14} />
            <Box
              as="span"
              style={{ fontSize: '13px', fontWeight: 600, color: '#68D391' }}
            >
              Completed
            </Box>
          </HStack>
        ) : (
          <Button
            scheme="gradient"
            size="sm"
            w="100%"
            onClick={handleComplete}
          >
            {completeLabel}
          </Button>
        )}
      </VStack>
    </VStack>
  );
};

const NodeDetailCard = ({
  selectedNode,
  nodeState,
  onNavigate,
  isManuallyCompleted,
  onToggleManualCompletion,
}: NodeDetailCardProps) => {
  const accentColor = selectedNode
    ? nodeState === 'completed'
      ? '#68D391'
      : '#B794F4'
    : undefined;

  return (
    <Box
      w="100%"
      minH={CARD_MIN_HEIGHT}
      p={5}
      borderRadius="xl"
      bg="whiteAlpha.50"
      border="1px solid"
      borderColor="whiteAlpha.100"
      borderLeft="3px solid"
      borderLeftColor={accentColor || 'whiteAlpha.200'}
      transition="all 0.2s ease"
    >
      {selectedNode ? (
        <NodeSelectedState
          node={selectedNode}
          nodeState={nodeState}
          onNavigate={onNavigate}
          isManuallyCompleted={isManuallyCompleted}
          onToggleManualCompletion={onToggleManualCompletion}
        />
      ) : (
        <WelcomeState />
      )}
    </Box>
  );
};

export default NodeDetailCard;
